import React, { useState, useEffect, useContext } from "react";
import {
  Modal,
  Button,
  Form,
  Row,
  Col,
  Spinner,
  Tab,
  Tabs,
  Alert,
  InputGroup,
} from "react-bootstrap";
import { useForm, Controller } from "react-hook-form";
import Select from "react-select";
import NumberFormat from "react-number-format";
import { IconUser, IconCheck, IconUpload, IconX, IconBuilding } from "@tabler/icons-react";
import {
  createUserSecure,
  updateUser,
  validateCPF,
  uploadUserPhoto,
} from "../services/UserServicesV2";
import departmentServices from "../../../services/DepartmentServices";
import NotificationsContext from "../../../contexts/NotificationsContext";
import { prepareUserData, getAvailableLevels, formatCPF } from "../utils";
import UserContext from "../../../contexts/UserContext";
import { formatPhone } from "../../Customers/utils";
import { validateCPF as clientValidateCPF } from "../../Customers/utils";

const UserModal = ({ show, onHide, user, isEdit, onSave }) => {
  const { setNotify } = useContext(NotificationsContext);
  const [loading, setLoading] = useState(false);
  const [cpfValidating, setCpfValidating] = useState(false);
  const [activeTab, setActiveTab] = useState("basic");
  const [photoPreview, setPhotoPreview] = useState(null);
  const [photoFile, setPhotoFile] = useState(null);

  // Estado para departamentos
  const [departments, setDepartments] = useState([]);
  const [loadingDepartments, setLoadingDepartments] = useState(false);

  // Assumindo que o usuário logado está disponível no contexto
  const { user: currentUser, canCreateUserWithLevel } = useContext(UserContext);

  const {
    control,
    register,
    handleSubmit,
    formState: { errors },
    reset,
    watch,
    setValue,
    clearErrors,
    trigger,
  } = useForm({
    shouldFocusError: false,
    defaultValues: {
      first_name: "",
      last_name: "",
      email: "",
      phone: "",
      mobile: "",
      cpf: "",
      birthday: "",
      superuser_level: 0,
      is_active: 1,
      department: null,
    },
  });

  const watchedLevel = watch("superuser_level");
  const watchedCpf = watch("cpf");

  // Buscar departamentos quando o modal abrir
  useEffect(() => {
    if (show) {
      fetchDepartments();
      if (isEdit && user) {
        populateForm(user);
      } else {
        reset({
          first_name: "",
          last_name: "",
          email: "",
          phone: "",
          mobile: "",
          cpf: "",
          birthday: "",
          superuser_level: 0,
          is_active: 1,
          department: null,
        });
        setPhotoPreview(null);
        setPhotoFile(null);
      }
    }
  }, [show, isEdit, user, reset]);

  // Buscar departamentos ativos
  const fetchDepartments = async () => {
    setLoadingDepartments(true);
    try {
      const data = await departmentServices.fetchDepartmentsSimple(true);
      setDepartments(data || []);
    } catch (error) {
      console.error("Erro ao buscar departamentos:", error);
      setDepartments([]);
    } finally {
      setLoadingDepartments(false);
    }
  };

  // Validação automática de CPF
  useEffect(() => {
    if (watchedCpf && watchedCpf.length >= 14) {
      handleCPFValidation(watchedCpf);
    }
  }, [watchedCpf]);

  const populateForm = (userData) => {
    const formData = {
      ...userData,
      phone: formatPhone(userData.phone),
      mobile: formatPhone(userData.mobile),
      cpf: formatCPF(userData.cpf),
      birthday: userData.birthday || "",
      department: userData.department || null,
    };

    reset(formData);

    if (userData.photo) {
      setPhotoPreview(userData.photo);
    }
  };

  const handleCPFValidation = async (cpf) => {
    if (!clientValidateCPF(cpf)) return;

    setCpfValidating(true);
    try {
      await validateCPF(cpf, watch("email"));
      clearErrors("cpf");
    } catch (error) {
      if (error.response?.status === 400) {
        // CPF já existe
        trigger("cpf");
      }
    } finally {
      setCpfValidating(false);
    }
  };

  const handlePhotoChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      // Validar tipo de arquivo
      if (!file.type.startsWith("image/")) {
        setNotify({
          title: "Erro!",
          message: "Por favor, selecione apenas arquivos de imagem",
          type: "error",
          visible: true,
        });
        return;
      }

      // Validar tamanho (máximo 5MB)
      if (file.size > 5 * 1024 * 1024) {
        setNotify({
          title: "Erro!",
          message: "A imagem deve ter no máximo 5MB",
          type: "error",
          visible: true,
        });
        return;
      }

      setPhotoFile(file);

      // Criar preview
      const reader = new FileReader();
      reader.onload = (e) => {
        setPhotoPreview(e.target.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const onSubmit = async (data) => {
    setLoading(true);
    try {
      // Validações adicionais
      if (data.superuser_level > 0 && !data.cpf) {
        setNotify({
          title: "Erro!",
          message: "CPF é obrigatório para superusuários",
          type: "error",
          visible: true,
        });
        setLoading(false);
        return;
      }

      if (data.cpf && !clientValidateCPF(data.cpf)) {
        setNotify({
          title: "Erro!",
          message: "CPF inválido",
          type: "error",
          visible: true,
        });
        setLoading(false);
        return;
      }

      if (
        !isEdit &&
        data.superuser_level > 0 &&
        !canCreateUserWithLevel(data.superuser_level)
      ) {
        setNotify({
          title: "Erro!",
          message: "Você não tem permissão para criar usuários com este nível",
          type: "error",
          visible: true,
        });
        setLoading(false);
        return;
      }

      // Preparar dados para envio
      const preparedData = prepareUserData(data);

      let response;
      if (isEdit) {
        response = await updateUser(user.id, preparedData);
      } else {
        response = await createUserSecure(preparedData);
      }

      // Upload da foto se houver
      if (photoFile && response.data) {
        try {
          await uploadUserPhoto(response.data.id || user.id, photoFile);
        } catch (photoError) {
          console.warn("Erro ao fazer upload da foto:", photoError);
          // Não bloqueia o sucesso do cadastro por causa da foto
        }
      }

      setNotify({
        title: "Sucesso!",
        message: `Usuário ${isEdit ? "atualizado" : "criado"} com sucesso!`,
        type: "success",
        visible: true,
      });

      onSave();
      handleClose();
    } catch (error) {
      console.error("Erro ao salvar usuário:", error);

      let errorMessage = "Erro ao salvar usuário";
      if (error.response?.data) {
        const errorData = error.response.data;
        if (typeof errorData === "object") {
          errorMessage = Object.values(errorData).flat().join(", ");
        } else {
          errorMessage = errorData.detail || errorData.message || errorMessage;
        }
      }

      setNotify({
        title: "Erro!",
        message: errorMessage,
        type: "error",
        visible: true,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    reset();
    setActiveTab("basic");
    setPhotoPreview(null);
    setPhotoFile(null);
    clearErrors();
    onHide();
  };

  const availableLevels = getAvailableLevels(currentUser.superuser_level);

  // Opções de departamento para o Select
  const departmentOptions = [
    { value: null, label: "Sem departamento" },
    ...(Array.isArray(departments) ? departments : []).map((dept) => ({
      value: dept.id,
      label: dept.name,
    })),
  ];

  return (
    <Modal
      show={show}
      onHide={handleClose}
      size="lg"
      backdrop="static"
      keyboard={false}
    >
      <Modal.Header closeButton>
        <Modal.Title className="d-flex align-items-center">
          <IconUser className="me-2" />
          {isEdit ? "Editar Usuário" : "Novo Usuário"}
        </Modal.Title>
      </Modal.Header>

      <Form onSubmit={handleSubmit(onSubmit)}>
        <Modal.Body>
          <Tabs
            activeKey={activeTab}
            onSelect={(k) => setActiveTab(k)}
            className="mb-3"
          >
            {/* Aba Informações do Usuário */}
            <Tab eventKey="basic" title="Informações do usuário">
              {/* Upload de Foto */}
              <Row className="mb-4">
                <Col md={12}>
                  <Form.Group>
                    <Form.Label>Foto de Perfil</Form.Label>
                    <div className="d-flex align-items-center">
                      {photoPreview && (
                        <img
                          src={photoPreview}
                          alt="Preview"
                          className="rounded-circle me-3"
                          width="64"
                          height="64"
                          style={{ objectFit: "cover" }}
                        />
                      )}
                      <div>
                        <input
                          type="file"
                          accept="image/*"
                          onChange={handlePhotoChange}
                          style={{ display: "none" }}
                          id="photo-upload"
                        />
                        <label htmlFor="photo-upload">
                          <Button
                            variant="outline-primary"
                            size="sm"
                            as="span"
                            className="me-2"
                          >
                            <IconUpload size={16} className="me-1" />
                            {photoPreview ? "Alterar" : "Selecionar"} Foto
                          </Button>
                        </label>
                        {photoPreview && (
                          <Button
                            variant="outline-danger"
                            size="sm"
                            onClick={() => {
                              setPhotoPreview(null);
                              setPhotoFile(null);
                            }}
                          >
                            <IconX size={16} />
                          </Button>
                        )}
                      </div>
                    </div>
                    <Form.Text className="text-muted">
                      Formatos aceitos: JPG, PNG, GIF. Tamanho máximo: 5MB
                    </Form.Text>
                  </Form.Group>
                </Col>
              </Row>

              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>
                      Nome <span className="text-danger">*</span>
                    </Form.Label>
                    <Form.Control
                      type="text"
                      {...register("first_name", {
                        required: "Nome é obrigatório",
                        minLength: {
                          value: 2,
                          message: "Nome deve ter pelo menos 2 caracteres",
                        },
                      })}
                      isInvalid={!!errors.first_name}
                    />
                    <Form.Control.Feedback type="invalid">
                      {errors.first_name?.message}
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Sobrenome</Form.Label>
                    <Form.Control
                      type="text"
                      {...register("last_name", {required: false})}
                    />
                  </Form.Group>
                </Col>
              </Row>

              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>
                      Email <span className="text-danger">*</span>
                    </Form.Label>
                    <Form.Control
                      type="email"
                      {...register("email", {
                        required: "E-mail é obrigatório!",
                        pattern: {
                          value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                          message: "Email inválido",
                        },
                      })}
                      isInvalid={!!errors.email}
                    />
                    <Form.Control.Feedback type="invalid">
                      {errors.email?.message}
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Data de Nascimento</Form.Label>
                    <Form.Control type="date" {...register("birthday")} />
                  </Form.Group>
                </Col>
              </Row>

              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>
                      Telefone Principal <span className="text-danger">*</span>
                    </Form.Label>
                    <Controller
                      name="phone"
                      control={control}
                      rules={{
                        required: "Telefone é obrigatório",
                      }}
                      render={({ field }) => (
                        <NumberFormat
                          {...field}
                          format="(##) #####-####"
                          mask="_"
                          className={`form-control ${
                            errors.phone ? "is-invalid" : ""
                          }`}
                          placeholder="(00) 00000-0000"
                        />
                      )}
                    />
                    {errors.phone && (
                      <div className="invalid-feedback d-block">
                        {errors.phone.message}
                      </div>
                    )}
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Telefone Secundário</Form.Label>
                    <Controller
                      name="mobile"
                      control={control}
                      render={({ field }) => (
                        <NumberFormat
                          {...field}
                          format="(##) #####-####"
                          mask="_"
                          className="form-control"
                          placeholder="(00) 00000-0000"
                        />
                      )}
                    />
                  </Form.Group>
                </Col>
              </Row>

              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>
                      CPF{" "}
                      {watchedLevel > 0 && (
                        <span className="text-danger">*</span>
                      )}
                    </Form.Label>
                    <InputGroup>
                      <Controller
                        name="cpf"
                        control={control}
                        rules={{
                          required:
                            watchedLevel > 0
                              ? "CPF é obrigatório para superusuários"
                              : false,
                          validate: (value) => {
                            if (!value) return true;
                            return clientValidateCPF(value) || "CPF inválido";
                          },
                        }}
                        render={({ field }) => (
                          <NumberFormat
                            {...field}
                            format="###.###.###-##"
                            mask="_"
                            className={`form-control ${
                              errors.cpf ? "is-invalid" : ""
                            }`}
                            placeholder="000.000.000-00"
                          />
                        )}
                      />
                      {cpfValidating && (
                        <InputGroup.Text>
                          <Spinner size="sm" animation="border" />
                        </InputGroup.Text>
                      )}
                    </InputGroup>
                    {errors.cpf && (
                      <div className="invalid-feedback d-block">
                        {errors.cpf.message}
                      </div>
                    )}
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Status</Form.Label>
                    <Form.Select {...register("is_active")}>
                      <option value={1}>Ativo</option>
                      <option value={0}>Inativo</option>
                    </Form.Select>
                  </Form.Group>
                </Col>
              </Row>

              {/* Campo de Departamento */}
              <Row>
                <Col md={12}>
                  <Form.Group className="mb-3">
                    <Form.Label>
                      <IconBuilding size={16} className="me-1" />
                      Departamento
                    </Form.Label>
                    <Controller
                      name="department"
                      control={control}
                      render={({ field }) => (
                        <Select
                          {...field}
                          options={departmentOptions}
                          value={departmentOptions.find(
                            (opt) => opt.value === field.value
                          )}
                          onChange={(option) =>
                            field.onChange(option?.value || null)
                          }
                          placeholder={
                            loadingDepartments
                              ? "Carregando departamentos..."
                              : "Selecione um departamento..."
                          }
                          isLoading={loadingDepartments}
                          isClearable
                          isSearchable
                          noOptionsMessage={() => "Nenhum departamento encontrado"}
                        />
                      )}
                    />
                    <Form.Text className="text-muted">
                      Opcional. Associe o usuário a um departamento para melhor organização.
                    </Form.Text>
                  </Form.Group>
                </Col>
              </Row>

              <Row>
                <Col md={12}>
                  <Form.Group className="mb-4">
                    <Form.Label>
                      Nível do Usuário <span className="text-danger">*</span>
                    </Form.Label>
                    <Controller
                      name="superuser_level"
                      control={control}
                      render={({ field }) => (
                        <Select
                          {...field}
                          options={[
                            { value: 0, label: "Usuário Comum" },
                            ...availableLevels,
                          ]}
                          value={[
                            { value: 0, label: "Usuário Comum" },
                            ...availableLevels,
                          ].find((opt) => opt.value === field.value)}
                          onChange={(option) =>
                            field.onChange(option?.value || 0)
                          }
                          placeholder="Selecione o nível..."
                          isSearchable={false}
                        />
                      )}
                    />
                  </Form.Group>
                </Col>
              </Row>

              {watchedLevel > 0 && (
                <Alert variant="info">
                  <strong>Nível {watchedLevel}:</strong>
                  <ul className="mb-0 mt-2">
                    {watchedLevel === 1 && (
                      <li>Acesso total ao sistema - pode gerenciar tudo</li>
                    )}
                    {watchedLevel === 2 && (
                      <>
                        <li>Pode gerenciar usuários de níveis inferiores</li>
                        <li>Acesso amplo a módulos de gestão</li>
                        <li>Visualiza relatórios e estatísticas</li>
                      </>
                    )}
                    {watchedLevel === 3 && (
                      <>
                        <li>Pode criar e gerenciar atendentes</li>
                        <li>Supervisiona operações e chats</li>
                        <li>Acesso a relatórios básicos</li>
                      </>
                    )}
                    {watchedLevel === 4 && (
                      <>
                        <li>Atendimento ao cliente via chat</li>
                        <li>Gestão básica de clientes</li>
                        <li>Acesso limitado a funcionalidades</li>
                      </>
                    )}
                  </ul>
                </Alert>
              )}

              <Alert variant="warning">
                <strong>Importante:</strong> As permissões específicas podem ser
                ajustadas individualmente após a criação do usuário através do
                gerenciador de permissões.
              </Alert>
            </Tab>
          </Tabs>
        </Modal.Body>

        <Modal.Footer>
          <Button variant="secondary" onClick={handleClose} disabled={loading}>
            Cancelar
          </Button>
          <Button
            type="submit"
            variant="primary"
            disabled={loading}
            className="d-flex align-items-center"
          >
            {loading ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                Salvando...
              </>
            ) : (
              <>
                <IconCheck size={18} className="me-1" />
                {isEdit ? "Atualizar" : "Criar"} Usuário
              </>
            )}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

export default UserModal;
